/* The chat's own surface — the conversation pane, its bubbles, cards, composer, popover, recorder,
 * drop target and lightbox — transcribed from the canon (`docs/design/czat-web-2026-10-05/`,
 * boards C1–C11 and P2–P8). The full screen's list column and header are the page's
 * (`pages/chat`); the panel's head is here because the panel is this slice's.
 *
 * Every value is a token. The handful the canon draws that KANON had no step for are the chat block
 * at the foot of `tokens/typography.css` and `tokens/spacing.css` (`--fs-chat-*`, `--chat-*`).
 *
 * The canon's voice note and recorder take the trainer's brand colour (`--accent`, which the brand
 * paints); its accent appears where the canon puts it — the unread dot and badge, the send button,
 * the marker's dot, the card tags' glyphs — and nowhere else. No shadow here is coloured. */

.num {
  font-variant-numeric: tabular-nums;
}

.sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* The canon's button reset, at zero specificity so every class below still decides its own look:
 * the browser's default border, fill and font never reach a chat control. */
:where(.pane, .dock, .lightbox) button {
  margin: 0;
  padding: 0;
  font: inherit;
  color: inherit;
  background: none;
  border: 0;
  cursor: pointer;
}

:where(.pane, .dock, .lightbox) button:focus-visible,
:where(.pane, .dock, .lightbox) a:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* ── Avatar ──────────────────────────────────────────────────────────────────────────────── */
.avatar {
  flex: none;
  display: inline-grid;
  place-items: center;
  border-radius: var(--r-circle);
  background: var(--bg-inset);
  color: var(--text-2);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-heading);
  border: var(--border-hairline) solid var(--border);
  object-fit: cover;
}

.avatar30 {
  width: 30px;
  height: 30px;
  font-size: var(--fs-eyebrow);
}

.avatar32 {
  width: 32px;
  height: 32px;
  font-size: var(--fs-caption);
}

.avatar36 {
  width: 36px;
  height: 36px;
  font-size: var(--fs-label);
}

.avatar40 {
  width: 40px;
  height: 40px;
  font-size: var(--fs-body);
}

.avatar44 {
  width: 44px;
  height: 44px;
  font-size: var(--fs-button);
}

.avatar60 {
  width: 60px;
  height: 60px;
  font-size: var(--fs-h2);
}

.avatar64 {
  width: 64px;
  height: 64px;
  font-size: var(--fs-chat-avatar-xl);
}

/* ── The pane ────────────────────────────────────────────────────────────────────────────── */
.pane {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.paneDock {
  background: var(--bg-sunken);
}

.threadHost {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.thread {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-4) var(--space-7) var(--space-14px);
  display: flex;
  flex-direction: column;
  gap: var(--space-3px);
  overscroll-behavior: contain;
}

.paneDock .thread {
  padding: var(--space-3) var(--space-20px);
}

.olderNote {
  align-self: center;
  margin: var(--space-2) 0;
  font-size: var(--fs-micro);
  color: var(--text-3);
}

.day {
  align-self: center;
  margin: var(--space-4) 0 var(--space-2);
}

.day span {
  display: inline-block;
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  color: var(--text-3);
  background: var(--bg-inset);
  padding: var(--space-1) var(--space-3);
  border-radius: var(--r-pill);
}

/* The new-messages marker: a pill between two hairlines that draw outward. */
.divider {
  --divider-line-in: linear-gradient(90deg, transparent, var(--border-2) 75%);
  --divider-line-out: linear-gradient(270deg, transparent, var(--border-2) 75%);

  display: flex;
  align-items: center;
  justify-content: center;
  margin: var(--space-22px) 0 var(--space-3);
  max-height: 80px;
  color: var(--text);
}

.dividerLine {
  flex: 1;
  height: 1px;
  background: var(--divider-line-in);
  transform-origin: 100% 50%;
  animation: divider-draw var(--chat-dur-draw) var(--ease) both;
}

.dividerLineEnd {
  background: var(--divider-line-out);
  transform-origin: 0 50%;
}

.dividerLabel {
  display: inline-flex;
  align-items: center;
  gap: var(--space-9px);
  height: 28px;
  padding: 0 var(--space-13px);
  border-radius: var(--r-pill);
  border: var(--border-hairline) solid var(--border-2);
  background: var(--bg-card);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  animation: chat-pop var(--dur-slow) var(--ease) both;
}

.dividerDot {
  width: 6px;
  height: 6px;
  border-radius: var(--r-circle);
  background: var(--accent);
}

.dividerWords {
  color: var(--text);
  font-weight: var(--fw-bold);
}

.dividerSince {
  padding-left: var(--space-9px);
  border-left: var(--border-hairline) solid var(--border-2);
  color: var(--text-3);
}

.dividerLeaving {
  animation: divider-out var(--chat-dur-leave) var(--ease) forwards;
}

/* ── Messages ────────────────────────────────────────────────────────────────────────────── */
.msg {
  display: flex;
  max-width: 72%;
  align-self: flex-start;
}

.paneDock .msg {
  max-width: 80%;
}

.mine {
  align-self: flex-end;
}

.first {
  margin-top: var(--space-9px);
}

.body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
}

.mine .body {
  align-items: flex-end;
}

.enter {
  animation: chat-rise var(--chat-dur-rise) var(--ease) both;
}

.mine.enter {
  transform-origin: 100% 100%;
}

.bubble {
  padding: var(--space-10px) var(--space-14px);
  border-radius: var(--radius-20px);
  font-size: var(--fs-body);
  line-height: var(--lh-chat-bubble);
  font-weight: var(--fw-medium);
  background: var(--bg-inset);
  color: var(--text);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.mine .bubble {
  background: var(--text);
  color: var(--bg);
}

.theirs.last .bubble {
  border-bottom-left-radius: var(--radius-7px);
}

.mine.last .bubble {
  border-bottom-right-radius: var(--radius-7px);
}

.caption {
  margin-top: var(--space-1);
}

/* An address in a message: the bubble's own ink, underlined, so it reads on both sides. */
.link {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: var(--space-3px);
}

/* A line the app wrote itself: centred and quiet, like the day separator, never a bubble. */
.system {
  align-self: center;
  max-width: 80%;
  margin: var(--space-2) 0;
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-chat-bubble);
  color: var(--text-2);
  text-align: center;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* Its hour, after the words, in the meta line's ink. */
.systemTime {
  margin-left: var(--space-2);
  font-size: var(--fs-chat-meta);
  color: var(--text-3);
}

.unsupported,
.gone {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  color: var(--text-2);
  font-style: italic;
}

.mine .unsupported,
.mine .gone {
  background: var(--bg-inset);
  color: var(--text-2);
}

.meta {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  margin: var(--space-3px) var(--space-1) 0;
  font-size: var(--fs-chat-meta);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
}

.tick {
  display: inline-flex;
  color: var(--text-3);
}

.failRow {
  display: flex;
  align-items: center;
  gap: var(--space-6px);
  margin: var(--space-1) var(--space-1) 0;
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--danger-ink);
}

.failRow button {
  color: var(--danger-ink);
  text-decoration: underline;
  text-underline-offset: 2px;
  font-weight: var(--fw-bold);
}

.failSep {
  width: 1px;
  height: 12px;
  background: var(--border-2);
}

/* ── Picture and film ────────────────────────────────────────────────────────────────────── */
.mediaWrap {
  display: flex;
  flex-direction: column;
  align-items: inherit;
}

.media {
  position: relative;
  display: block;
  padding: var(--space-1);
  border-radius: var(--radius-lg);
  background: var(--bg-inset);
  overflow: hidden;
}

.mediaPicture {
  display: block;
  width: 248px;
  height: 186px;
  object-fit: cover;
  border-radius: var(--radius-md);
}

.mediaBlank {
  background: var(--bg-chip);
}

.media:hover .mediaPicture {
  filter: var(--chat-media-hover);
}

.playDot {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 52px;
  height: 52px;
  translate: -50% -50%;
  border-radius: var(--r-circle);
  background: var(--chat-veil-strong);
  color: var(--chat-on-veil);
  display: grid;
  place-items: center;
}

.upload {
  position: absolute;
  inset: var(--space-1);
  border-radius: var(--radius-md);
  background: var(--chat-veil);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: var(--space-2);
  padding: var(--space-3);
  color: var(--chat-on-veil);
}

.uploadLabel {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  text-align: left;
}

.uploadBar {
  height: 4px;
  border-radius: var(--radius-hair);
  background: var(--chat-veil-track);
  overflow: hidden;
}

.uploadBar i {
  display: block;
  height: 100%;
  background: var(--chat-on-veil);
  transition: width var(--dur-fast) var(--ease-linear);
}

/* ── Voice note ──────────────────────────────────────────────────────────────────────────── */
.voice {
  display: flex;
  align-items: center;
  gap: var(--space-10px);
  padding: var(--space-2) var(--space-3) var(--space-2) var(--space-2);
  border-radius: var(--chat-r-voice);
  background: var(--bg-inset);
  min-width: 270px;
}

.mine .voice {
  background: var(--text);
  color: var(--bg);
}

.voicePlay {
  width: 36px;
  height: 36px;
  border-radius: var(--r-circle);
  display: grid;
  place-items: center;
  background: var(--accent);
  color: var(--on-accent);
  flex: none;
}

.voicePlay:disabled {
  opacity: 0.5;
  cursor: default;
}

.voiceBars {
  display: flex;
  align-items: center;
  gap: var(--chat-gap-bars);
  height: 30px;
  flex: 1;
}

.voiceBars i {
  display: block;
  width: 3px;
  border-radius: var(--radius-hair);
  background: var(--text-3);
  transition: background var(--dur-fast);
}

.voiceBars i.voiceBarOn {
  background: var(--accent);
}

.voiceDuration {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  min-width: 32px;
}

.voiceSpeed {
  font-size: var(--fs-caption);
  font-weight: var(--fw-extrabold);
  padding: var(--space-3px) var(--space-6px);
  border-radius: var(--radius-7px);
  border: var(--border-hairline) solid var(--border-2);
  color: var(--text-2);
}

.mine .voiceSpeed {
  color: inherit;
  opacity: 0.7;
}

/* ── Cards ───────────────────────────────────────────────────────────────────────────────── */
.card {
  width: 268px;
  border-radius: var(--radius-20px);
  border: var(--border-hairline) solid var(--border);
  background: var(--bg-card);
  box-shadow: var(--shadow-card);
  padding: var(--space-3) var(--space-13px) var(--space-13px);
  overflow: hidden;
}

.mine .card {
  border-color: transparent;
}

.cardTag {
  display: flex;
  align-items: center;
  gap: var(--space-6px);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-day);
  text-transform: uppercase;
  color: var(--text-3);
}

.cardTag svg {
  color: var(--accent);
}

.cardRow {
  display: flex;
  align-items: center;
  gap: var(--space-11px);
  margin: var(--space-10px) 0 var(--space-3);
  width: 100%;
  text-align: left;
}

.thumb {
  width: 50px;
  height: 50px;
  border-radius: var(--r-field);
  object-fit: cover;
  flex: none;
}

.thumbBlank {
  display: grid;
  place-items: center;
  background: var(--bg-inset);
  color: var(--text-3);
}

.clipRow:disabled {
  cursor: default;
}

.clipThumb {
  position: relative;
  overflow: hidden;
  display: block;
  background: var(--bg-inset);
}

.clipThumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.playMini {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  color: var(--chat-on-veil);
  background: var(--chat-veil-soft);
}

.cardText {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.cardTitle {
  font-size: var(--fs-field);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-heading);
  line-height: var(--lh-chat-card);
}

.cardMeta {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  margin-top: var(--space-2px);
}

.cardGone {
  font-size: var(--fs-label);
  line-height: var(--lh-chat-prose);
  color: var(--text-3);
}

.cta {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-6px);
  padding: var(--space-9px);
  border-radius: var(--radius-12px);
  background: var(--text);
  color: var(--bg);
  font-weight: var(--fw-extrabold);
  font-size: var(--fs-note);
  text-decoration: none;
}

.cta svg {
  opacity: 0.7;
}

.cta:hover {
  filter: var(--chat-cta-hover);
}

.planCard {
  padding: 0;
}

.cover {
  position: relative;
  height: 120px;
}

.coverPicture {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.coverTag {
  position: absolute;
  top: var(--space-10px);
  left: var(--space-10px);
  display: inline-flex;
  align-items: center;
  gap: var(--space-5px);
  padding: var(--space-1) var(--space-9px);
  border-radius: var(--r-pill);
  background: var(--chat-veil);
  color: var(--chat-on-veil);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
}

.planBody {
  padding: var(--space-3) var(--space-13px) var(--space-13px);
}

.planBody .cardMeta {
  margin-bottom: var(--space-3);
}

.summaryHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  margin: var(--space-9px) 0 var(--space-6px);
}

.sets {
  flex: none;
  font-size: var(--fs-chat-meta);
  font-weight: var(--fw-bold);
  padding: var(--space-3px) var(--space-2);
  border-radius: var(--r-pill);
  background: var(--bg-inset);
  color: var(--text-2);
}

.highlight {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-7px) 0;
  border-top: var(--border-hairline) solid var(--border);
}

.highlight:last-of-type {
  margin-bottom: var(--space-10px);
}

.highlightName {
  flex: 1;
  min-width: 0;
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.highlightTop {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.pr {
  font-size: var(--fs-chat-pr);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-chip);
  padding: var(--space-2px) var(--space-5px);
  border-radius: var(--radius-xs);
  background: var(--pr);
  color: var(--on-pr);
}

.reflection {
  font-style: italic;
  font-size: var(--fs-note);
  line-height: var(--lh-chat-note);
  color: var(--text-2);
  margin: var(--space-6px) 0 var(--space-10px);
}

/* ── Typing ──────────────────────────────────────────────────────────────────────────────── */
.typingRow {
  animation: chat-rise var(--dur-base) var(--ease) both;
}

.typing {
  display: flex;
  gap: var(--space-5px);
  padding: var(--space-13px) var(--space-15px);
}

.typingDot {
  width: 7px;
  height: 7px;
  border-radius: var(--r-circle);
  background: var(--text-3);
  animation: chat-blink var(--chat-dur-blink) var(--ease-linear) infinite;
}

.typingDot:nth-child(2) {
  animation-delay: var(--chat-delay-blink-2);
}

.typingDot:nth-child(3) {
  animation-delay: var(--chat-delay-blink-3);
}

.typingLabel {
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  margin: var(--space-3px) var(--space-1) 0;
}

/* ── Jump and banner ─────────────────────────────────────────────────────────────────────── */
.jump {
  position: absolute;
  left: 50%;
  bottom: var(--space-14px);
  translate: -50% 0;
  display: flex;
  align-items: center;
  gap: var(--space-7px);
  padding: var(--space-2) var(--space-14px);
  border-radius: var(--r-pill);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  box-shadow: var(--shadow-card);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  z-index: 2;
  animation: chat-pop var(--dur-base) var(--ease) both;
}

/* The unread figure: the brand's dot and the number in ink, no fill (owner decision). */
.badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-6px);
  font-size: var(--fs-note);
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

.badge::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: var(--r-circle);
  background: var(--accent);
}

.banner {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  background: var(--warn-soft);
  color: var(--warn-ink);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  animation: chat-fade var(--dur-base) var(--ease) both;
}

.wait {
  color: var(--warn-ink);
}

/* ── States ──────────────────────────────────────────────────────────────────────────────── */
.threadSkeleton {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: var(--space-4) var(--space-7) var(--space-14px);
}

.paneDock .threadSkeleton {
  padding: var(--space-3) var(--space-20px);
}

.skeletonBubble {
  height: 40px;
  border-radius: var(--radius-20px);
  margin-top: var(--space-10px);
}

.skTheirs { align-self: flex-start; }
.skMine { align-self: flex-end; }

.skCard {
  width: 268px;
  height: 170px;
}

.w30 { width: 30%; }
.w40 { width: 40%; }
.w50 { width: 50%; }
.w60 { width: 60%; }
.w70 { width: 70%; }

.threadState,
.threadEmpty {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-10px);
  text-align: center;
  padding: var(--space-10) var(--space-6);
  font-size: var(--fs-chat-prose);
  color: var(--text-2);
}

.threadState b {
  color: var(--text);
  font-size: var(--fs-field);
}

.threadEmpty h2 {
  margin: var(--space-6px) 0 0;
  font-size: var(--fs-h2);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-title);
  color: var(--text);
}

.paneDock .threadEmpty h2 {
  font-size: var(--fs-chat-dock-empty);
}

.threadEmpty p {
  margin: 0;
  max-width: 340px;
  line-height: var(--lh-chat-prose);
}

.starters {
  display: flex;
  gap: var(--space-2);
  margin-top: var(--space-14px);
  flex-wrap: wrap;
  justify-content: center;
}

.starter {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-10px) var(--space-14px);
  border-radius: var(--radius-md);
  border: var(--border-hairline) solid var(--border-2);
  background: var(--bg-card);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  color: var(--text);
}

.starter:hover {
  background: var(--bg-inset);
}

.frozen {
  border-top: var(--border-hairline) solid var(--border);
  padding: var(--space-4) var(--space-6);
  display: flex;
  flex-direction: column;
  gap: var(--space-3px);
  font-size: var(--fs-label-md);
  color: var(--text-2);
  text-align: center;
}

.frozen b {
  color: var(--text);
  font-size: var(--fs-body);
}

/* ── Composer ────────────────────────────────────────────────────────────────────────────── */
.composer {
  position: relative;
  border-top: var(--border-hairline) solid var(--border);
  padding: var(--space-10px) var(--space-4);
}

.paneDock .composer {
  background: var(--bg-sunken);
  padding: var(--space-10px) var(--space-14px);
}

.composerRow {
  display: flex;
  align-items: flex-end;
  gap: var(--space-2);
}

.iconButton {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-12px);
  display: grid;
  place-items: center;
  color: var(--text-2);
  flex: none;
  transition:
    background var(--dur-fast),
    color var(--dur-fast);
}

.iconButton:hover {
  background: var(--hover-tint);
  color: var(--text);
}

.iconButtonOn {
  background: var(--bg-chip);
  color: var(--text);
}

.field {
  flex: 1;
  display: flex;
  background: var(--bg-inset);
  border-radius: var(--chat-r-field);
  padding: var(--space-10px) var(--space-4);
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: transparent;
  transition: border-color var(--dur-fast);
}

.field:focus-within {
  border-color: var(--border-3);
}

.field textarea {
  flex: 1;
  border: 0;
  background: transparent;
  outline: none;
  resize: none;
  font: inherit;
  font-size: var(--fs-body);
  line-height: var(--lh-chat-note);
  color: var(--text);
  max-height: 170px;
  padding: 0;
}

.field textarea::placeholder {
  color: var(--text-3);
}

.send {
  width: 42px;
  height: 42px;
  border-radius: var(--r-circle);
  display: grid;
  place-items: center;
  background: var(--bg-inset);
  color: var(--text-2);
  flex: none;
  transition:
    background var(--dur-base) var(--ease),
    color var(--dur-base),
    transform var(--dur-fast);
}

.send:active {
  transform: scale(0.93);
}

.sendOn {
  background: var(--accent);
  color: var(--on-accent);
}

.sendOn:disabled {
  opacity: 0.6;
}

.mic:hover {
  color: var(--text);
}

.micRefusal {
  display: flex;
  align-items: flex-start;
  gap: var(--space-10px);
  margin-bottom: var(--space-10px);
  padding: var(--space-10px) var(--space-3);
  border-radius: var(--radius-8px);
  background: var(--warn-soft);
  color: var(--warn-ink);
  animation: chat-fade var(--dur-base) var(--ease) both;
}

.micRefusal > svg {
  flex: none;
  margin-top: var(--space-3px);
}

.micRefusalText {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-3px);
  font-size: var(--fs-label);
}

.micRefusalClose {
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-8px);
  display: grid;
  place-items: center;
  color: var(--warn-ink);
}

.micRefusalClose:hover {
  background: var(--hover-tint);
}

.composerHint {
  display: flex;
  justify-content: space-between;
  gap: var(--space-3);
  margin: var(--space-7px) var(--space-12) 0;
  font-size: var(--fs-caption);
  color: var(--text-3);
}

/* ── Tray ────────────────────────────────────────────────────────────────────────────────── */
.tray {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  margin: var(--space-2px) 0 var(--space-10px) var(--space-12);
}

.trayItem {
  display: flex;
  align-items: center;
  gap: var(--space-10px);
  padding: var(--space-6px);
  border-radius: var(--radius-md);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  animation: chat-pop var(--dur-base) var(--ease) both;
}

.trayThumb {
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-sm);
  overflow: hidden;
  display: block;
  flex: none;
  background: var(--bg-inset);
}

.trayThumb img,
.trayThumb video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.trayText {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.trayName {
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  max-width: 160px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.traySize {
  font-size: var(--fs-micro);
  color: var(--text-3);
}

.trayRemove {
  width: 28px;
  height: 28px;
  border-radius: var(--radius-8px);
  display: grid;
  place-items: center;
  color: var(--text-3);
}

.trayRemove:hover {
  background: var(--hover-tint);
  color: var(--text);
}

.trayHint {
  font-size: var(--fs-micro);
  color: var(--text-3);
}

/* ── Recorder ────────────────────────────────────────────────────────────────────────────── */
.rec {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  background: var(--bg-inset);
  border-radius: var(--radius-xl);
  padding: var(--space-5px) var(--space-5px) var(--space-5px) var(--space-2);
  animation: chat-pop var(--dur-base) var(--ease) both;
}

.recCancel {
  width: 36px;
  height: 36px;
  border-radius: var(--r-circle);
  display: grid;
  place-items: center;
  color: var(--text-2);
}

.recCancel:hover {
  background: var(--hover-tint);
  color: var(--danger-ink);
}

.recDot {
  width: 9px;
  height: 9px;
  border-radius: var(--r-circle);
  background: var(--danger);
  animation: chat-rec-dot var(--chat-dur-rec-dot) var(--ease-in-out) infinite;
}

.recClock {
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  min-width: 34px;
}

.recBars {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2px);
  height: 32px;
  overflow: hidden;
}

.recBars i {
  display: block;
  width: 3px;
  border-radius: var(--radius-hair);
  background: var(--accent);
  flex: none;
}

.recBarsStill {
  justify-content: flex-start;
}

.recStop {
  width: 42px;
  height: 42px;
  border-radius: var(--r-circle);
  display: grid;
  place-items: center;
  background: var(--accent);
  color: var(--on-accent);
}

/* ── Attachment popover ──────────────────────────────────────────────────────────────────── */
.pop {
  position: absolute;
  left: var(--space-3);
  bottom: calc(100% - var(--space-1));
  width: min(440px, calc(100% - var(--space-6)));
  max-height: min(520px, 70vh);
  overflow-y: auto;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-20px);
  box-shadow: var(--shadow-pop);
  padding: var(--space-3);
  z-index: 3;
  animation: chat-pop var(--dur-base) var(--ease) both;
  transform-origin: 0 100%;
}

.popHead {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2px) var(--space-2px) var(--space-10px);
  font-size: var(--fs-button);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-heading);
}

.popHead span {
  flex: 1;
}

.popIcon {
  width: 30px;
  height: 30px;
  border-radius: var(--radius-mark);
  display: grid;
  place-items: center;
  color: var(--text-2);
}

.popIcon:hover {
  background: var(--hover-tint);
  color: var(--text);
}

.tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-2);
}

.tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  padding: var(--space-3);
  border-radius: var(--radius-16px);
  background: var(--bg-inset);
  transition:
    background var(--dur-fast),
    transform var(--dur-fast);
}

.tile:hover {
  background: var(--bg-chip);
}

.tile:active {
  transform: scale(0.97);
}

.tileIcon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-12px);
  display: grid;
  place-items: center;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  color: var(--text);
}

.tileLabel {
  margin-top: var(--space-10px);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
}

.tileHint {
  font-size: var(--fs-micro);
  color: var(--text-3);
  margin-top: var(--space-1px);
}

.popFoot {
  font-size: var(--fs-micro);
  color: var(--text-3);
  padding: var(--space-10px) var(--space-1) var(--space-2px);
}

.pickNote,
.pickEmpty {
  font-size: var(--fs-note);
  color: var(--text-3);
  padding: var(--space-1) var(--space-1) var(--space-2);
}

.pickSearch {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  background: var(--bg-inset);
  border-radius: var(--radius-12px);
  padding: var(--space-9px) var(--space-3);
  margin: var(--space-2px) 0 var(--space-2);
  color: var(--text-3);
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: transparent;
}

.pickSearch:focus-within {
  border-color: var(--border-3);
}

.pickSearch input {
  flex: 1;
  border: 0;
  background: transparent;
  outline: none;
  font: inherit;
  font-size: var(--fs-body);
  color: var(--text);
}

.pickList {
  max-height: 290px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
}

.pickItem {
  display: flex;
  align-items: center;
  gap: var(--space-11px);
  padding: var(--space-7px) var(--space-2);
  border-radius: var(--radius-12px);
  width: 100%;
  text-align: left;
}

.pickItem:hover,
.pickItem:focus-visible {
  background: var(--hover-tint);
}

.pickThumb {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-11px);
  object-fit: cover;
  flex: none;
}

.pickText {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.pickTitle {
  font-size: var(--fs-chat-prose);
  font-weight: var(--fw-bold);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pickMeta {
  font-size: var(--fs-label);
  color: var(--text-3);
  margin-top: var(--space-1px);
}

.pickGo {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  opacity: 0;
  transition: opacity var(--dur-fast);
}

.pickItem:hover .pickGo,
.pickItem:focus-visible .pickGo {
  opacity: 1;
}

/* ── Drop target ─────────────────────────────────────────────────────────────────────────── */
.drop {
  --drop-veil: color-mix(in srgb, var(--bg) 88%, transparent);

  position: absolute;
  inset: 0;
  z-index: 4;
  background: var(--drop-veil);
  display: flex;
  padding: var(--space-4);
  animation: chat-fade var(--dur-fast) var(--ease) both;
}

.dropBox {
  flex: 1;
  border: var(--border-focus) dashed var(--border-3);
  border-radius: var(--radius-xl);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-10px);
  color: var(--text-2);
  pointer-events: none;
  animation: chat-pop var(--dur-base) var(--ease) both;
}

.dropTitle {
  font-size: var(--fs-mode);
  font-weight: var(--fw-extrabold);
  color: var(--text);
  letter-spacing: var(--ls-title);
}

.dropHint {
  font-size: var(--fs-label-md);
}

/* ── Lightbox ────────────────────────────────────────────────────────────────────────────── */
.lightboxScrim {
  position: fixed;
  inset: 0;
  z-index: 40;
  background: var(--scrim);
  backdrop-filter: var(--blur-scrim);
}

.lightbox {
  position: fixed;
  left: 50%;
  top: 50%;
  translate: -50% -50%;
  z-index: 41;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-pop);
  padding: var(--space-14px);
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  gap: var(--space-10px);
  animation: chat-pop var(--dur-base) var(--ease) both;
}

.lightboxHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: 0 var(--space-1);
}

.lightboxTitle {
  margin: 0;
  font-weight: var(--fw-bold);
  font-size: var(--fs-body);
}

.lightboxMedia {
  display: block;
  max-width: min(900px, 86vw);
  max-height: min(680px, calc(90vh - 80px));
  border-radius: var(--radius-md);
  background: var(--player-stage);
  object-fit: contain;
}

/* ── The Zadania panel ───────────────────────────────────────────────────────────────────── */
.dock {
  flex-direction: column;
  animation: dock-in var(--dur-drawer) var(--ease) both;
}

.dockWaiting {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.dockHead {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-height: 69px;
  padding: var(--space-14px) var(--space-14px) var(--space-14px) var(--space-20px);
  border-bottom: var(--border-hairline) solid var(--border);
}

.dockWho {
  flex: 1;
  min-width: 0;
}

.dockName {
  flex: 1;
  font-size: var(--fs-h3);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-heading);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dockSub {
  display: flex;
  align-items: center;
  gap: var(--space-6px);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  margin-top: var(--space-2px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.others {
  height: 30px;
  display: inline-flex;
  align-items: center;
  gap: var(--space-7px);
  padding: 0 var(--space-10px);
  border-radius: var(--radius-mark);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  text-decoration: none;
  white-space: nowrap;
}

.others:hover {
  background: var(--hover-tint);
  color: var(--text);
}

.others i {
  width: 6px;
  height: 6px;
  border-radius: var(--r-circle);
  background: var(--accent);
}

/* A send that did not go: the bubble dims under its „Nie wysłano” row. */
.msg.failed .bubble,
.msg.failed .media,
.msg.failed .card,
.msg.failed .voice {
  opacity: 0.6;
}

/* ── Motion ──────────────────────────────────────────────────────────────────────────────── */
@keyframes chat-rise {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes chat-pop {
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.97);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes chat-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes chat-blink {
  0%,
  80%,
  100% {
    opacity: 0.25;
  }

  40% {
    opacity: 1;
  }
}

@keyframes chat-rec-dot {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.25;
  }
}

@keyframes divider-draw {
  from {
    transform: scaleX(0);
  }

  to {
    transform: scaleX(1);
  }
}

@keyframes divider-out {
  to {
    opacity: 0;
    max-height: 0;
    margin-top: 0;
    margin-bottom: 0;
    transform: translateY(-4px);
  }
}

@keyframes dock-in {
  from {
    transform: translateX(104%);
  }

  to {
    transform: none;
  }
}

/* The report's field: the form dialog's input, grown to a paragraph. */
.reportField {
  height: auto;
  min-height: 96px;
  padding: var(--space-10px) var(--space-14px);
  resize: vertical;
  line-height: var(--lh-chat-note);
}

/* The panel takes focus while it waits for its conversation; the frame needs no ring for that. */
.dock:focus {
  outline: none;
}
